import { useTranslation } from "react-i18next";
import { Globe } from "lucide-react";
import { SUPPORTED_LANGS, setLang, getLang, type Lang } from "@/i18n";
import { updateLanguagePreference } from "@/lib/language";

const LABELS: Record<Lang, string> = { en: "EN", vi: "VI" };

/**
 * Compact language toggle (EN / VI). Applies the choice immediately via the
 * i18n singleton + localStorage, and best-effort syncs it to the logged-in
 * account so backend emails match. Styled for the dark chrome used across the
 * marketing nav and the app/admin sidebars.
 */
export function LanguageSwitcher({ className }: { className?: string }) {
  const { i18n, t } = useTranslation("common");
  const current = getLang();

  const choose = (lang: Lang) => {
    if (lang === current) return;
    setLang(lang);
    void updateLanguagePreference(lang);
  };

  return (
    <div
      className={className}
      role="group"
      aria-label={t("lang.switch")}
      data-testid="language-switcher"
      style={{
        display: "inline-flex",
        alignItems: "center",
        gap: 2,
        padding: 3,
        borderRadius: 999,
        border: "1px solid rgba(255,255,255,.14)",
        background: "rgba(255,255,255,.05)",
      }}
    >
      <Globe size={13} style={{ margin: "0 3px", opacity: 0.6, color: "currentColor" }} aria-hidden />
      {SUPPORTED_LANGS.map((lang) => {
        const active = lang === i18n.language || (lang === "en" && !SUPPORTED_LANGS.includes(i18n.language as Lang));
        return (
          <button
            key={lang}
            type="button"
            onClick={() => choose(lang)}
            data-testid={`lang-${lang}`}
            aria-pressed={active}
            style={{
              cursor: "pointer",
              border: "none",
              borderRadius: 999,
              padding: "3px 9px",
              fontSize: 11.5,
              fontWeight: 600,
              lineHeight: 1,
              color: active ? "#0c0a09" : "currentColor",
              background: active ? "#FF8A2B" : "transparent",
              transition: "background .15s, color .15s",
            }}
          >
            {LABELS[lang]}
          </button>
        );
      })}
    </div>
  );
}
